"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
var _classCallCheck2 = _interopRequireDefault(require("@babel/runtime/helpers/classCallCheck"));
var _createClass2 = _interopRequireDefault(require("@babel/runtime/helpers/createClass"));
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _require = require('../utils/helpers'),
  cleanParams = _require.cleanParams,
  trimNonBreakingSpaces = _require.trimNonBreakingSpaces,
  encodeURIComponentRFC3986 = _require.encodeURIComponentRFC3986,
  stringify = _require.stringify;

// Create URL from supplied intent (term) and parameters
function createAgentUrl(intent, parameters, options) {
  var apiKey = options.apiKey,
    version = options.version,
    sessionId = options.sessionId,
    clientId = options.clientId,
    userId = options.userId,
    segments = options.segments,
    testCells = options.testCells,
    agentServiceUrl = options.agentServiceUrl,
    assistantServiceUrl = options.assistantServiceUrl;
  var queryParams = {
    c: version
  };
  queryParams.key = apiKey;
  queryParams.i = clientId;
  queryParams.s = sessionId;
  var serviceUrl = agentServiceUrl || assistantServiceUrl;

  // Validate intent is provided
  if (!intent || typeof intent !== 'string') {
    throw new Error('intent is a required parameter of type string');
  }

  // Validate domain is provided
  if (!parameters.domain || typeof parameters.domain !== 'string') {
    throw new Error('parameters.domain is a required parameter of type string');
  }

  // Pull test cells from options
  if (testCells) {
    Object.keys(testCells).forEach(function (testCellKey) {
      queryParams["ef-".concat(testCellKey)] = testCells[testCellKey];
    });
  }

  // Pull user segments from options
  if (segments && segments.length) {
    queryParams.us = segments;
  }

  // Pull user id from options and ensure string
  if (userId) {
    queryParams.ui = String(userId);
  }
  if (parameters) {
    var domain = parameters.domain,
      numResultsPerPage = parameters.numResultsPerPage;

    // Pull domain from parameters
    if (domain) {
      queryParams.domain = domain;
    }

    // Pull results number from parameters
    if (numResultsPerPage) {
      queryParams.num_results_per_page = numResultsPerPage;
    }
  }

  // eslint-disable-next-line no-underscore-dangle
  queryParams._dt = Date.now();
  queryParams = cleanParams(queryParams);
  var queryString = stringify(queryParams);
  var cleanedQuery = intent.replace(/^\//, '|'); // For compatibility with backend API

  return "".concat(serviceUrl, "/v1/intent/").concat(encodeURIComponentRFC3986(trimNonBreakingSpaces(cleanedQuery)), "?").concat(queryString);
}

// Add event listeners to custom SSE that pushes data to the stream
function setupEventListeners(eventSource, controller, eventTypes) {
  var addListener = function addListener(type) {
    eventSource.addEventListener(type, function (event) {
      var data = JSON.parse(event.data);
      controller.enqueue({
        type: type,
        data: data
      }); // Enqueue data into the stream
    });
  };

  // Set up listeners for all event types except END
  Object.values(eventTypes).forEach(function (type) {
    if (type !== eventTypes.END) {
      addListener(type);
    }
  });

  // Handle the END event separately to close the stream
  eventSource.addEventListener(eventTypes.END, function () {
    controller.close(); // Close the stream
    eventSource.close(); // Close the EventSource connection
  });

  // Handle errors from the EventSource
  // eslint-disable-next-line no-param-reassign
  eventSource.onerror = function (error) {
    controller.error(error); // Pass the error to the stream
    eventSource.close(); // Close the EventSource connection
  };
}

/**
 * Interface to agent SSE.
 * Replaces the previous Assistant module.
 *
 * @module agent
 * @inner
 * @returns {object}
 */
var Agent = /*#__PURE__*/function () {
  function Agent(options) {
    (0, _classCallCheck2["default"])(this, Agent);
    this.options = options || {};
  }
  return (0, _createClass2["default"])(Agent, [{
    key: "getAgentResultsStream",
    value:
    /**
     * Retrieve agent results from EventStream
     *
     * @function getAgentResultsStream
     * @description Retrieve a stream of agent results from Constructor.io API
     * @param {string} intent - Intent to use to perform an intent based recommendations
     * @param {object} [parameters] - Additional parameters to refine result set
     * @param {string} [parameters.domain] - domain name e.g. swimming sports gear, groceries
     * @param {number} [parameters.numResultsPerPage] - The total number of results to return
     * @returns {ReadableStream} Returns a ReadableStream.
     * @example
     * const readableStream = constructorio.agent.getAgentResultsStream('I want to get shoes', {
     *     domain: "nike_sportswear",
     * });
     * const reader = readableStream.getReader();
     * const { value, done } = await reader.read();
     */
    function getAgentResultsStream(query, parameters) {
      var eventSource;
      var readableStream;
      try {
        var requestUrl = createAgentUrl(query, parameters, this.options);

        // Create an EventSource that connects to the Server Sent Events API
        eventSource = new EventSource(requestUrl);

        // Create a readable stream that data will be pushed into
        readableStream = new ReadableStream({
          // To be called on stream start
          start: function start(controller) {
            // Listen to events emitted from ASA Server Sent Events and push data to the ReadableStream
            setupEventListeners(eventSource, controller, Agent.EventTypes);
          },
          // To be called on stream cancelling
          cancel: function cancel() {
            // Close the EventSource connection when the stream is prematurely canceled
            eventSource.close();
          }
        });
      } catch (e) {
        if (readableStream) {
          var _readableStream;
          (_readableStream = readableStream) === null || _readableStream === void 0 || _readableStream.cancel();
        } else {
          var _eventSource;
          // If the stream was not successfully created, close the EventSource directly
          (_eventSource = eventSource) === null || _eventSource === void 0 || _eventSource.close();
        }
        throw new Error(e.message);
      }
      return readableStream;
    }
  }]);
}();
(0, _defineProperty2["default"])(Agent, "EventTypes", {
  START: 'start',
  // Denotes the start of the stream
  GROUP: 'group',
  // Represents a semantic grouping of search results, optionally having textual explanation
  SEARCH_RESULT: 'search_result',
  // Represents a set of results with metadata (used to show results with search refinements)
  ARTICLE_REFERENCE: 'article_reference',
  // Represents a set of content with metadata
  RECIPE_INFO: 'recipe_info',
  // Represents recipes' auxiliary information like cooking times & serving sizes
  RECIPE_INSTRUCTIONS: 'recipe_instructions',
  // Represents recipe instructions
  SERVER_ERROR: 'server_error',
  // Server Error event
  IMAGE_META: 'image_meta',
  // This event type is used for enhancing recommendations with media content such as images
  END: 'end' // Represents the end of data stream
});
module.exports = Agent;
module.exports.createAgentUrl = createAgentUrl;
module.exports.setupEventListeners = setupEventListeners;